import { faFiles } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '@mui/material';
import MUIBox from '@mui/material/Box';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { StyledMUIButton } from 'src/components/Button';
import { Toast } from 'src/components/Toast';
import { spacing } from 'src/constants/spacing';
import { ProductFileData } from './mapSrcToProductFiles';
import { FlexRow } from './styles';
import { ProductFile } from './Uploader';

type DropzoneProps = Readonly<{
  existingFiles: ProductFileData[];
  setProductFiles: React.Dispatch<React.SetStateAction<ProductFile[]>>;
  openModal: () => void;
}>;

const initialProductFile: Omit<ProductFile, 'file'> = {
  name: '',
  uploadState: 'initial',
  products: [],
  description: '',
  size: 0,
};

export function Dropzone({ existingFiles, setProductFiles, openModal }: DropzoneProps) {
  const [duplicateFileNames, setDuplicateFileNames] = useState<string[]>([]);
  const [hasReachedUploadLimit, setHasReachedUploadLimit] = useState(false);

  const { t } = useTranslation('uploads');

  const onDrop = useCallback(
    (newFiles: File[]) => {
      const duplicates: string[] = [];

      const filesToAdd = newFiles
        .filter((newFile) => {
          const fileAlreadyPresent = existingFiles.find(
            /* istanbul ignore next */ (file) => file.name === newFile.name
          );
          /* istanbul ignore next */
          if (fileAlreadyPresent) {
            duplicates.push(newFile.name);
          }
          return !fileAlreadyPresent;
        })
        .map((file) => ({
          ...initialProductFile,
          file,
          name: file.name,
          size: file.size,
        }));

      setDuplicateFileNames(duplicates);
      setProductFiles((prevFiles) => [...prevFiles, ...filesToAdd]);
      if (filesToAdd.length <= 10) {
        openModal();
      } else {
        setProductFiles([]);
        setHasReachedUploadLimit(true);
      }
    },
    [existingFiles, openModal, setProductFiles]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <>
      <DragAndDropContainer {...getRootProps()} data-testid="drag-and-drop">
        <input {...getInputProps()} data-testid="dropzone" />
        {isDragActive ? (
          /* istanbul ignore next */
          <p>{t('dropFilesHereOr')} ...</p>
        ) : (
          <DropzoneInner>
            <p>{t('dropFilesHereOr')}</p>
            <FlexRow>
              <StyledMUIButton data-testid="choose-files" startIcon={<FontAwesomeIcon icon={faFiles} size="sm" />}>
                {t('uploadFiles')}
              </StyledMUIButton>
            </FlexRow>
          </DropzoneInner>
        )}
      </DragAndDropContainer>
      <Toast
        dependency={duplicateFileNames.length}
        severity="error"
        title={t('status.error.fileAlreadyUploaded')}
        message={t('status.error.duplicateFilesInStaging', { files: duplicateFileNames.join(' ') })}
        onClose={/* istanbul ignore next */ () => setDuplicateFileNames([])}
      />
      <Toast
        dependency={hasReachedUploadLimit}
        severity="error"
        title={t('status.error.limitReached')}
        message={t('status.error.compressFiles')}
        onClose={/* istanbul ignore next */ () => setHasReachedUploadLimit(false)}
      />
    </>
  );
}

const DropzoneInner = styled('div')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const dropzoneHeight = '10rem';

const DragAndDropContainer = styled(MUIBox)<{ isDragActive?: boolean }>`
  min-height: ${dropzoneHeight};
  margin: ${spacing.largeSpacing} 0;
  padding: ${spacing.largeSpacing};
  text-align: center;
  border-radius: ${spacing.xSmallSpacing};
  border: 4px dashed
    ${({ theme, isDragActive }) =>
      isDragActive ? /* istanbul ignore next */ theme.palette.success.main : theme.palette.grey[400]};
  background-color: ${({ theme }) => theme.palette.grey[200]};
  color: ${({ theme }) => theme.palette.common.black};
  outline: none;
  transition: border 0.2s ease-in-out;
  position: relative;
`;
